<template>
  <div class="workspace-selector">

    <!-- Multiple workspaces: rounded boxed search-select -->
    <v-menu
      v-if="workspaces.length > 1"
      offset-y
      :min-width="menuWidth"
      :close-on-content-click="false"
      v-model="menuOpen"
    >
      <template v-slot:activator="{ on, attrs }">
        <div
          class="workspace-selector__box d-flex align-center"
          v-on="on"
          v-bind="attrs"
        >
          <account-avatar :name="currentName" :size="30" />
          <span class="workspace-selector__name text-truncate mx-2">{{ currentName }}</span>
          <v-icon small class="text--secondary">fa-caret-down</v-icon>
        </div>
      </template>

      <v-card>
        <div v-if="showSearch" class="pa-2">
          <v-text-field
            v-model="search"
            placeholder="Search workspaces"
            prepend-inner-icon="fa-magnifying-glass"
            dense
            outlined
            hide-details
            clearable
            autofocus
          />
        </div>

        <v-list dense class="pt-0" style="max-height: 360px; overflow-y: auto;">
          <v-list-item
            v-for="ws in filteredWorkspaces"
            :key="ws.id"
            :href="ws.url"
            :input-value="ws.id + '' === currentId + ''"
            color="primary"
          >
            <account-avatar :name="ws.name" :size="28" class="mr-3" />
            <v-list-item-content>
              <v-list-item-title>{{ ws.name }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item v-if="!filteredWorkspaces.length" disabled>
            <v-list-item-content>
              <v-list-item-title class="text--secondary">No matches</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>

    <!-- Single workspace: boxed, no dropdown -->
    <div v-else class="workspace-selector__box d-flex align-center">
      <account-avatar :name="currentName" :size="30" />
      <span class="workspace-selector__name text-truncate ml-2">{{ currentName }}</span>
    </div>

  </div>
</template>

<script>
import AccountAvatar from './AccountAvatar.vue'

export default {
  components: {
    AccountAvatar
  },
  data () {
    return {
      menuOpen: false,
      search: ''
    }
  },
  computed: {
    currentId () {
      return this.$store.getters.account?.id
    },
    currentName () {
      return this.$store.getters.account?.name || ''
    },
    menuWidth () {
      return this.$vuetify.breakpoint.xs ? '90vw' : 260
    },
    workspaces () {
      const accounts = [...(this.$store.getters.switchableAccounts || [])]
      const current = this.$store.getters.account
      // A superuser can be viewing an account that isn't in their accounts list.
      if (current?.id && !accounts.find(a => a.id + '' === current.id + '')) {
        accounts.push(current)
      }

      // Build the target subdomain URL for each workspace (switching is a full
      // navigation to the account's subdomain — there is no in-app switch action).
      const host = window.location.host
      const protocol = window.location.protocol
      let port = ''
      let hostname = host
      if (host.includes(':')) {
        port = ':' + host.split(':')[1]
        hostname = host.split(':')[0]
      }
      const baseDomain = hostname.split('.').slice(1).join('.')

      return accounts
        .filter(account => !!account?.subdomain)
        .map(account => ({
          ...account,
          url: `${protocol}//${account.subdomain}.${baseDomain}${port}`
        }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    },
    showSearch () {
      return this.workspaces.length > 6
    },
    filteredWorkspaces () {
      const q = (this.search || '').trim().toLowerCase()
      if (!q) return this.workspaces
      return this.workspaces.filter(ws => (ws.name || '').toLowerCase().includes(q))
    }
  },
  watch: {
    menuOpen (open) {
      if (!open) this.search = ''
    }
  }
}
</script>

<style scoped>
.workspace-selector {
  width: 100%;
}
.workspace-selector__box {
  cursor: pointer;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.16);
  border-radius: 10px;
  padding: 7px 10px;
}
.workspace-selector__name {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
}
</style>

<style>
.theme--dark .workspace-selector__box {
  border-color: rgba(255, 255, 255, 0.18);
}
</style>
